import React, { useState } from 'react';
import { Button, Row, Col, Modal } from 'react-bootstrap';
import { Link, generatePath } from 'react-router-dom';
import { Routes } from '#Routes';
import moment from 'moment';
import { ArrowNarrowRightIcon } from '@heroicons/react/solid';
import { useTranslation } from 'react-i18next';
import { Document } from '#atoms';

export const HealthReportsGallery = ({ data = [], readOnly = false }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  const closeModal = () => {
    setShowModal(false);
    setSelectedReport(null);
  };

  const openModal = (report) => {
    setSelectedReport(report);
    setShowModal(true);
  };

  return (
    <>
      <Row>
        {data?.map?.((document) => (
          <Col lg={6} key={document.id} className='mb-3'>
            <Document isMiniature>
              <Document.Title>
                {t('health-reports-gallery.title')}
                <div className='h6'>
                  {t('health-reports-gallery.dated')}
                  <span> {moment(document.created).format('y-MM-DD HH:mm')}</span>
                </div>
              </Document.Title>
              <Document.Subtitle>{document.report_title}</Document.Subtitle>
              <Document.Loader
                isLoading={document.process_status === 'IN_PROGRESS'}
                text={t('health-reports-gallery.processing')}
              />
              <Document.Truncated>{document.report_summary}</Document.Truncated>
              <Document.Actions className='mt-auto'>
                {readOnly ? (
                  <Button variant='logo-green' onClick={() => openModal(document)}>
                    {t('health-reports-gallery.view-report')}
                    <ArrowNarrowRightIcon className='icon icon-xs ms-2' />
                  </Button>
                ) : (
                  <Button
                    as={Link}
                    variant='logo-green'
                    to={generatePath(Routes.HealthReport.path, { id: document.id })}
                  >
                    {t('health-reports-gallery.view-report')}
                    <ArrowNarrowRightIcon className='icon icon-xs ms-2' />
                  </Button>
                )}
              </Document.Actions>
            </Document>
          </Col>
        ))}
      </Row>
      {readOnly && selectedReport && (
        <Modal size={'xl'} show={showModal} onHide={closeModal}>
          <Modal.Header closeButton>
            <Modal.Title>{t('health-report-page.title')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Document>
              <Document.Loader
                isLoading={selectedReport?.process_status === 'IN_PROGRESS'}
                text={t('health-report-page.processing')}
              />

              <Document.Title badge={{ bg: 'success', text: 'AI' }}>
                {t('health-report-page.title')}
              </Document.Title>

              <Document.Information>
                <>
                  <Col xs={12} className='mb-2'>
                    {t('health-report-page.processing-finished')}{' '}
                    <span className='fw-bold'>
                      {selectedReport?.process_status === 'FINISHED'
                        ? t('health-report-page.yes')
                        : t('health-report-page.no')}
                    </span>
                  </Col>
                </>
                <>
                  <Col xs={6} className='mb-2'>
                    {t('health-report-page.date-created')}
                  </Col>
                  <Col xs={6} className='mb-2'>
                    {moment(selectedReport?.created).format('y-MM-DD HH:mm')}
                  </Col>
                </>
              </Document.Information>

              <Document.Subtitle>{selectedReport?.report_title}</Document.Subtitle>

              <Document.Table title={t('health-report-page.lab-report')} content={selectedReport?.report_labs} />

              <Document.Text title={t('health-report-page.diag-report')} content={selectedReport?.report_diags} />

              <Document.Text
                title={t('health-report-page.summary-report')}
                content={selectedReport?.report_final}
              />
              {selectedReport?.question && (
                <Document.Text title={t('health-report-page.question')} content={selectedReport?.question} />
              )}

              {selectedReport?.answer && (
                <Document.Text title={t('health-report-page.answer')} content={selectedReport?.answer} />
              )}
              <div className='mt-4' style={{ textAlign: 'right' }}>
                {t('medical-document.document-not-approved')}
              </div>
            </Document>
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={closeModal}>
              {t('document-gallery.close')}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};
